/**
 * Cabecalho do detalhe da empresa.
 *
 * Nao usa `HeroBanner` porque o icone da faixa e um glifo do kit, e aqui o
 * que identifica a pagina e o LOGO da empresa (decisao do dono: logo e nome
 * em cima). Reproduz a anatomia da faixa -- fundo `bg-hero` a 7%, `h1` com
 * `id="titulo-da-pagina"`, selos ao lado do titulo -- para a pagina continuar
 * igual as outras aos olhos e ao leitor de tela.
 *
 * O selo de saude entra assim que o detalhe carrega (`saude` nulo antes):
 * e a primeira coisa que a equipe quer saber ao abrir uma empresa.
 */

import { Badge, Button, CompanyLogo, LinkButton } from '../../../components/ui';
import { formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { SaudeDaEmpresa } from '../types';
import { MODELO_COBRANCA, STATUS_EMPRESA } from '../vocabulario';
import { SeloDeSaude } from './SaudeEmpresa';

export function CabecalhoEmpresa({
  company,
  periodo,
  salvo,
  saude,
  voltarPara,
  onAbrirCrm,
  onEditar,
}: {
  readonly company: CompanySummary;
  readonly periodo: string;
  /** Mostra o selo "Salvo na previa" logo depois de uma edicao local. */
  readonly salvo: boolean;
  /** `null` enquanto o detalhe nao carregou. */
  readonly saude: SaudeDaEmpresa | null;
  /** Lista de empresas com o recorte de onde a pessoa veio. */
  readonly voltarPara: string;
  readonly onAbrirCrm: () => void;
  readonly onEditar: () => void;
}): JSX.Element {
  const status = STATUS_EMPRESA[company.status];
  const modelo = MODELO_COBRANCA[company.billingModel];
  return (
    <header className="relative overflow-hidden rounded-2xl border border-line bg-surface px-5 py-5 shadow-soft">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-hero opacity-[0.07]" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <LinkButton to={voltarPara} variant="ghost" icon="seta-esq" keepSearch={false}>
            <span className="sr-only">Voltar para a lista de empresas</span>
          </LinkButton>
          <CompanyLogo company={company} size="xl" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 id="titulo-da-pagina" className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                {company.name}
              </h1>
              <Badge tone={status.tone} dot>
                {status.label}
              </Badge>
              {saude === null ? null : <SeloDeSaude saude={saude} />}
              <span title={`Modelo de cobrança: ${modelo.label}`}>
                <Badge tone={modelo.tone}>{modelo.curto}</Badge>
              </span>
              <span role="status">
                {salvo ? (
                  <Badge tone="good" icon="check">
                    Salvo na prévia
                  </Badge>
                ) : null}
              </span>
            </div>
            <p className="mt-0.5 max-w-2xl text-sm text-ink-muted">
              {company.segment === '' ? 'Segmento não informado' : company.segment} · comissão de <strong className="text-ink">{formatPercentFromBps(company.feeBps)}</strong> · {periodo}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon="editar" onClick={onEditar}>
            Editar
          </Button>
          <Button variant="primary" icon="painel" onClick={onAbrirCrm}>
            Abrir CRM
          </Button>
        </div>
      </div>
    </header>
  );
}
